import React from 'react';
import { useMetaTags } from '../hooks/useMetaTags';
export function CANoticePage() {
  useMetaTags({
    title: 'CA Notice at Collection | FindOnlineDegrees',
    description: 'California Consumer Privacy Act notice for FindOnlineDegrees.org'
  });
  return <div className="min-h-screen bg-gray-100" data-id="element-700">
      {/* Hero Section */}
      <section className="w-full bg-[#1a2332] text-white py-16 px-4" data-id="element-701">
        <div className="max-w-4xl mx-auto text-center" data-id="element-702">
          <h1 className="text-3xl md:text-5xl font-bold mb-4" data-id="element-703">
            CA Notice At Collection
          </h1>
          <p className="text-gray-300 text-sm" data-id="element-704">Last updated: January 2026</p>
        </div>
      </section>

      {/* Content Section */}
      <section className="max-w-4xl mx-auto px-4 py-12" data-id="element-705">
        <div className="bg-white rounded-lg shadow-sm p-8 md:p-12 space-y-8" data-id="element-706">
          <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-707">
            <p data-id="element-708">
              This privacy notice for FindOnlineDegrees.org describes how and
              why we might collect, store, use, and/or share your information
              when you use our services, such as when you:
            </p>
            <ul className="list-disc pl-6 space-y-2" data-id="element-709">
              <li data-id="element-710">
                Visit our website, or any website of ours that links to this
                privacy notice
              </li>
              <li data-id="element-711">
                Engage with us in other related ways, including any sales,
                marketing, or events
              </li>
            </ul>
            <p className="font-semibold" data-id="element-712">
              Questions or concerns? Reading this privacy notice will help you
              understand your privacy rights and choices. If you do not agree
              with our policies and practices, please do not use our services.
            </p>
          </div>

          <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-713">
            <p data-id="element-714">
              If you are a California resident, the California Consumer Privacy
              Act ("CCPA") requires us to provide you with the following notice
              with information about:
            </p>
            <ul className="list-disc pl-6 space-y-2" data-id="element-715">
              <li data-id="element-716">
                the purpose for which we use each category of "personal
                information" (as defined in the CCPA) we collect; and
              </li>
              <li data-id="element-717">
                the categories of third parties to which we (a) disclose such
                personal information for a business purpose, (b) "share"
                personal information for "cross-context behavioral advertising,"
                and/or (c) "sell" such personal information.
              </li>
            </ul>
          </div>

          {/* Categories of Personal Information */}
          <div data-id="element-718">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-719">
              Categories of Personal Information We Collect
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-720">
              <p data-id="element-721">
                The table below describes the categories of personal information
                we have collected from California consumers in the preceding 12
                months, the sources from which we collected it, our business or
                commercial purposes for collecting it, and the categories of
                third parties to whom we disclose, share, or sell that
                information.
              </p>
            </div>

            <div className="overflow-x-auto mt-6" data-id="element-722">
              <table className="min-w-full border border-gray-300" data-id="element-723">
                <thead className="bg-gray-50" data-id="element-724">
                  <tr data-id="element-725">
                    <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900" data-id="element-726">
                      Category of Personal Information
                    </th>
                    <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900" data-id="element-727">
                      Examples
                    </th>
                    <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900" data-id="element-728">
                      Collected?
                    </th>
                    <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900" data-id="element-729">
                      Sources
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white" data-id="element-730">
                  <tr data-id="element-731">
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-732">
                      <strong data-id="element-733">Identifiers</strong>
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-734">
                      Name, email address, phone number, IP address, device ID
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-735">
                      Yes
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-736">
                      Directly from you via forms, automatically via cookies
                    </td>
                  </tr>
                  <tr className="bg-gray-50" data-id="element-737">
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-738">
                      <strong data-id="element-739">Personal Information (CA Civil Code)</strong>
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-740">
                      Name, contact information, education history
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-741">
                      Yes
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-742">
                      Directly from you via forms
                    </td>
                  </tr>
                  <tr data-id="element-743">
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-744">
                      <strong data-id="element-745">Commercial Information</strong>
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-746">
                      Records of services considered or obtained
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-747">
                      Yes
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-748">
                      From your interactions with our services
                    </td>
                  </tr>
                  <tr className="bg-gray-50" data-id="element-749">
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-750">
                      <strong data-id="element-751">Internet/Network Activity</strong>
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-752">
                      Browsing history, search history, interaction with website
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-753">
                      Yes
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-754">
                      Automatically via cookies and analytics
                    </td>
                  </tr>
                  <tr data-id="element-755">
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-756">
                      <strong data-id="element-757">Geolocation Data</strong>
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-758">
                      Approximate location based on IP address
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-759">
                      Yes
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-760">
                      Automatically via IP address
                    </td>
                  </tr>
                  <tr className="bg-gray-50" data-id="element-761">
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-762">
                      <strong data-id="element-763">Inferences</strong>
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-764">
                      Preferences, characteristics, behavior
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-765">
                      Yes
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-sm" data-id="element-766">
                      Derived from your activity on our services
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Business Purposes */}
          <div data-id="element-767">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-768">
              Business or Commercial Purposes for Collection
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-769">
              <p data-id="element-770">
                We collect and use personal information for the following
                purposes:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-771">
                <li data-id="element-772">To provide, improve, and administer our services</li>
                <li data-id="element-773">
                  To communicate with you, including responding to inquiries
                </li>
                <li data-id="element-774">To ensure security and fraud prevention</li>
                <li data-id="element-775">To comply with legal obligations</li>
                <li data-id="element-776">To deliver targeted advertising and marketing</li>
                <li data-id="element-777">To analyze and improve our website performance</li>
                <li data-id="element-778">To connect you with educational institutions</li>
              </ul>
            </div>
          </div>

          {/* Disclosure of Personal Information */}
          <div data-id="element-779">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-780">
              Disclosure of Personal Information
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-781">
              <p data-id="element-782">
                We may disclose your personal information to the following
                categories of third parties:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-783">
                <li data-id="element-784">Educational institutions and their partners</li>
                <li data-id="element-785">Service providers (analytics, hosting, marketing)</li>
                <li data-id="element-786">Advertising partners and networks</li>
                <li data-id="element-787">Legal and regulatory authorities when required</li>
                <li data-id="element-788">Business partners in connection with business transfers</li>
              </ul>
            </div>
          </div>

          {/* Your Rights */}
          <div data-id="element-789">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-790">
              Your California Privacy Rights
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-791">
              <p data-id="element-792">As a California resident, you have the following rights:</p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-793">
                <li data-id="element-794">
                  <strong data-id="element-795">Right to Know:</strong> Request disclosure of the
                  categories and specific pieces of personal information we have
                  collected about you.
                </li>
                <li data-id="element-796">
                  <strong data-id="element-797">Right to Delete:</strong> Request deletion of your
                  personal information, subject to certain exceptions.
                </li>
                <li data-id="element-798">
                  <strong data-id="element-799">Right to Correct:</strong> Request correction of
                  inaccurate personal information.
                </li>
                <li data-id="element-800">
                  <strong data-id="element-801">Right to Opt-Out:</strong> Opt out of the "sale" or
                  "sharing" of your personal information for cross-context
                  behavioral advertising.
                </li>
                <li data-id="element-802">
                  <strong data-id="element-803">Right to Limit:</strong> Limit the use and disclosure
                  of sensitive personal information (if applicable).
                </li>
                <li data-id="element-804">
                  <strong data-id="element-805">Right to Non-Discrimination:</strong> You will not
                  receive discriminatory treatment for exercising your privacy
                  rights.
                </li>
              </ul>
            </div>
          </div>

          {/* How to Exercise Rights */}
          <div data-id="element-806">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-807">
              How to Exercise Your Rights
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-808">
              <p data-id="element-809">
                To exercise your rights under the CCPA, please contact us
                through the information provided on our website. We will verify
                your identity before processing your request and respond within
                the timeframes required by law.
              </p>
            </div>
          </div>

          {/* Contact Us */}
          <div data-id="element-810">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-811">
              Contact Us
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-812">
              <p data-id="element-813">
                If you have questions about this notice or wish to exercise your
                California privacy rights, please contact us through the
                information provided on our website.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>;
}